import { db, type CachedClient, type CachedMachine } from './db.ts'

/**
 * The read snapshot (SPEC section 2): the full catalogue and client list, kept in
 * IndexedDB so global search works with no signal. The service worker already
 * caches pages you've opened; this is the part it can't do — a search over
 * everything, not just what you happened to visit online.
 */

function norm(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
}

/** Refreshes the snapshot from the server. Called after login and on reconnect. */
export async function refreshSnapshot(): Promise<void> {
  if (!navigator.onLine) return
  try {
    // The list endpoints return their full working set (hundreds of rows) — ample
    // for this dealer's snapshot. No pagination to stitch together.
    const [clientsRes, machinesRes] = await Promise.all([
      fetch('/api/clients', { credentials: 'same-origin' }),
      fetch('/api/machines', { credentials: 'same-origin' }),
    ])
    if (clientsRes.ok) {
      const { clients } = (await clientsRes.json()) as { clients: RawClient[] }
      const rows: CachedClient[] = clients.map((c) => ({
        id: c.id,
        name: c.name,
        city: c.city ?? null,
        nip: c.nip ?? null,
        phone: c.phone ?? null,
        searchable: norm([c.name, c.city, c.nip, c.phone].filter(Boolean).join(' ')),
      }))
      await db.clients.clear()
      await db.clients.bulkPut(rows)
    }
    if (machinesRes.ok) {
      const { machines } = (await machinesRes.json()) as { machines: RawMachine[] }
      const rows: CachedMachine[] = machines.map((m) => ({
        id: m.id,
        model: m.model,
        catalogCode: m.catalogCode,
        manufacturerName: m.manufacturerName,
        category: m.category,
        searchable: norm([m.model, m.catalogCode, m.manufacturerName].filter(Boolean).join(' ')),
      }))
      await db.machines.clear()
      await db.machines.bulkPut(rows)
    }
  } catch {
    // Offline again mid-refresh — keep whatever snapshot we already have.
  }
}

interface RawClient {
  id: string
  name: string
  city?: string | null
  nip?: string | null
  phone?: string | null
}
interface RawMachine {
  id: string
  model: string
  catalogCode: string
  manufacturerName: string
  category: string
}

export interface OfflineHit {
  kind: 'klient' | 'maszyna'
  id: string
  title: string
  subtitle: string
  url: string
}

/** Client-side search over the snapshot — the offline half of Ctrl+K. */
export async function searchOffline(term: string): Promise<OfflineHit[]> {
  const q = norm(term.trim())
  if (q.length < 2) return []
  const [clients, machines] = await Promise.all([
    db.clients.filter((c) => c.searchable.includes(q)).limit(8).toArray(),
    db.machines.filter((m) => m.searchable.includes(q)).limit(8).toArray(),
  ])
  return [
    ...clients.map((c): OfflineHit => ({
      kind: 'klient',
      id: c.id,
      title: c.name,
      subtitle: [c.city, c.nip ? `NIP ${c.nip}` : null, c.phone].filter(Boolean).join(' · '),
      url: `/klienci/${c.id}`,
    })),
    ...machines.map((m): OfflineHit => ({
      kind: 'maszyna',
      id: m.id,
      title: m.model,
      subtitle: [m.manufacturerName, m.catalogCode].filter(Boolean).join(' · '),
      url: `/katalog/${m.id}`,
    })),
  ]
}
